import React, { useEffect, useState, useMemo } from 'react';
import { apiFetch } from '../bizsuite-app';

/**
 * RegionPicker — two cascading dropdowns: Country → Region.
 *
 * Country list is metadata_lookups where category='country'.
 * Region list is metadata_lookups where category='region', filtered by
 * the selected country's id (parent_id match).
 *
 * Picking a Region calls onChange(regionId). Picking a Country only
 * narrows the region list — it does not call onChange until a region
 * is selected, unless you opt into that with onCountryChange.
 *
 * Props:
 *   value             — region_id (UUID) | null
 *   onChange          — (regionId) => void
 *   countryId         — optional controlled country id
 *   onCountryChange   — optional (countryId) => void (for forms that store country separately)
 *   disabled          — bool
 *   label             — optional label rendered above the row
 *   required          — bool (visual asterisk on label)
 *   placeholder       — placeholder for the region dropdown
 *   compact           — render the two dropdowns inline as a single row
 */
export default function RegionPicker({
  value, onChange,
  countryId, onCountryChange,
  disabled, label, required, placeholder, compact = true,
}) {
  const [countries, setCountries]           = useState([]);
  const [regions, setRegions]               = useState([]);
  const [loading, setLoading]               = useState(true);
  const [internalCountry, setInternalCountry] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cRes, rRes] = await Promise.all([
          apiFetch('/api/metadata/lookups?category=country'),
          apiFetch('/api/metadata/lookups?category=region'),
        ]);
        if (cancelled) return;
        if (cRes.ok) setCountries((await cRes.json()).lookups || []);
        if (rRes.ok) setRegions((await rRes.json()).lookups || []);
      } catch (_) { /* ignore */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // When `value` (region_id) is set externally, derive the country from
  // its parent_id so the country dropdown stays in sync. This makes the
  // picker round-trip cleanly when editing existing records.
  useEffect(() => {
    if (!value) { if (internalCountry !== null) setInternalCountry(null); return; }
    const r = regions.find(x => x.id === value);
    if (r && r.parent_id !== internalCountry) setInternalCountry(r.parent_id);
  }, [value, regions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effective country id used to filter regions: explicit prop wins, else internal
  const activeCountryId = countryId !== undefined && countryId !== null ? countryId : internalCountry;

  const sortedCountries = useMemo(
    () => [...countries].sort((a, b) => String(a.name).localeCompare(String(b.name))),
    [countries]
  );
  const filteredRegions = useMemo(() => {
    const list = activeCountryId
      ? regions.filter(r => r.parent_id === activeCountryId)
      : regions;
    return [...list].sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [regions, activeCountryId]);

  function pickCountry(newId) {
    if (onCountryChange) onCountryChange(newId || null);
    else setInternalCountry(newId || null);
    // If the current region doesn't belong to the new country, clear it.
    if (value) {
      const r = regions.find(x => x.id === value);
      if (r && newId && r.parent_id !== newId) onChange(null);
      if (!newId) onChange(null);
    }
  }

  return (
    <div style={wrap}>
      {label && (
        <span style={lbl}>{label}{required && <span style={{ color:'#EF4444', marginLeft:4 }}>*</span>}</span>
      )}
      {/* minmax(0, 1fr) lets the cells shrink below their content's natural
          width — without it, long country names (e.g. "United Arab Emirates")
          force the inner grid to overflow the parent cell, which on the
          contract editor pushes the whole page past its max-width. */}
      <div style={{ display:'grid', gridTemplateColumns: compact ? 'minmax(0, 1fr) minmax(0, 1fr)' : 'minmax(0, 1fr)', gap: 8, minWidth: 0 }}>
        <select
          value={activeCountryId || ''}
          onChange={e => pickCountry(e.target.value || null)}
          disabled={disabled || loading}
          style={inp}
          title="Country"
        >
          <option value="">{loading ? 'Loading…' : '— Country —'}</option>
          {sortedCountries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select
          value={value || ''}
          onChange={e => onChange(e.target.value || null)}
          disabled={disabled || loading || !activeCountryId}
          style={{ ...inp, opacity: !activeCountryId ? 0.7 : 1 }}
          title="Region"
        >
          <option value="">
            {loading ? 'Loading…' : (!activeCountryId ? 'Pick country first' : (placeholder || '— Region —'))}
          </option>
          {filteredRegions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
      </div>
    </div>
  );
}

const wrap = { display:'flex', flexDirection:'column', gap:4, minWidth: 0 };
const lbl  = { fontSize:11, color:'#94A3B8', letterSpacing:'0.4px', textTransform:'uppercase', fontWeight:600 };
const inp  = { padding:'8px 12px', borderRadius:6, border:'1px solid rgba(255,255,255,0.10)',
               background:'rgba(15,23,42,0.6)', color:'#E5E7EB', fontSize:13, outline:'none', width:'100%' };
